/**
 * Everything the Points page does that is not rendering.
 *
 * The rules stay on the server, in `points_logic` -- the two different
 * definitions of "stale", the two different expiry windows, the four-arm
 * member-number cascade. This module sends requests and maps the server's
 * semantic tones onto colours.
 *
 * **Nothing here caches, stores, or logs a member number.** `reveal` returns
 * one and hands it straight to the caller; the page holds it in component
 * state for a single render and never puts it in localStorage, a URL, or a
 * console call.
 */

import { ago } from "../../../shared/captureFormat";
import { authedFetch } from "./api";
import { orThrow } from "./http";

import type { Tone } from "./tone";
export { toneClass, type Tone } from "./tone";

export interface AccountRow {
  program_name: string;
  /** The ledger's program slug, which the registry and a capture run share. Null
   *  only for a row built without it. */
  program_slug: string | null;
  label: string;
  captions: [Tone, string][];
  balance_text: string;
  delta_text: string | null;
  basis: "authoritative" | "derived" | "none";
  staleness: string;
  value: number | null;
  value_text: string | null;
  cpp_text: string | null;
  no_valuation_reason: string | null;
  held_count: number;
  /** The program's kind, in AwardWallet's vocabulary: the page's grouping. */
  kind: string;
  elite_status: string | null;
  /** [tone, text] under the server's own thresholds. Never markup. */
  expires: [Tone, string] | null;
  /** How `expires` is known: the issuer's own date, an estimate from the
   *  program's inactivity rule, no expiry at all, or not known. */
  expires_basis: ExpiryBasis;
  /** The raw day behind `expires`, stated or estimated, for the edit field. */
  expires_on: string | null;
  /** Who set a stated date: "browser", "manual" or "awardwallet". */
  expires_source: string | null;
  /** Which elite tier or co-brand card exempts these points ("Exempt: Gold
   *  Elite"), or which would; null when the rule has no exemptions. */
  expires_note?: string | null;
  age: [Tone, string];
  /** Points moved since the last authoritative reading, or null. */
  change: number | null;
  error_caption: string | null;
}

export interface InstrumentRow {
  name: string;
  /** Whether a booking code is stored. The code itself is never in this
   *  payload: it is fetched on demand, like a member number. */
  code_stored: boolean;
  spent: boolean;
  memo: string | null;
  amount: string;
  /** The stored amount is not zero. Sent by the server so the page never
   *  parses the formatted string. */
  nonzero: boolean;
  review_note: string | null;
  book_by: string;
  travel_by: string | null;
  status_text: string | null;
  used_key: string;
  deadline: [Tone, string];
  travel: [Tone, string] | null;
  /** The amount before the last change Revert would undo, and the day it
   *  changed. Absent when there is nothing to revert. */
  revert?: { was: string; on: string } | null;
}

/** What several credits under one name add up to, and the soonest deadline among them. */
export interface InstrumentTotal {
  name: string;
  total: string;
  count: number;
  next: [Tone, string];
}

/** One group's subtotal, over every account whatever the filter. */
export interface KindTotal {
  kind: string;
  total: number;
  unvalued: number;
  count: number;
}

/**
 * One row of the account-management section.
 *
 * Separate from `PointsAccount` because the server sends a separate, UNFILTERED
 * list for it: the live page's "Show" radio narrows the balance cards and does
 * not touch the "Programs & accounts" list below them, so reveal and delete
 * stay reachable for every account whatever the filter says.
 */
export interface ManageAccount {
  id: number;
  label: string;
  program_name: string;
  member_number_status: [Tone, string] | null;
  reveal_disabled: boolean;
  masked: string;
}

export interface PointsAccount {
  id: number;
  row: AccountRow;
  /** Balance exactly 0 and no live credit with value: the server's
   *  `is_empty_account`. Folded into "Empty accounts" below the groups. */
  empty?: boolean;
  instruments: {
    title: string;
    expanded: boolean;
    soon: number;
    active_count: number;
    /** One per name and unit that two or more live, nonzero credits share. */
    totals: InstrumentTotal[];
    items: InstrumentRow[];
  };
  activity: { recent: string[]; superseded: string[] };
  /** The browser-captured reading the balance currently rests on, which
   *  "Undo this reading" rejects. Absent when capture did not write it. */
  capture?: { event_id: number; as_of: string } | null;
}

export type ExpiryBasis = "stated" | "estimated" | "never" | "unknown";

/**
 * A balance that needs acting on: its expiry, stated or estimated, falls inside
 * the server's 30-day window, or a stated date has already passed with points
 * still on the account (`past`). Soonest first, over every account.
 */
export interface AttentionItem {
  account_id: number;
  program_name: string;
  account_label: string;
  /** YYYY-MM-DD. */
  date: string;
  basis: "stated" | "estimated";
  /** Negative when `past`. */
  days_left: number;
  past: boolean;
}

/** The Expiration panel's Rule line, one per basis. */
export function expiryRuleText(basis: ExpiryBasis): string {
  if (basis === "stated") return "The program's own date, from a capture or set by hand";
  if (basis === "estimated") return "Estimated from the last balance change the ledger saw";
  if (basis === "never") return "These points do not expire";
  return "No expiration rule recorded for this program";
}

/**
 * "61,200" out of a row's "61,200 pts", or null when the balance is unread.
 * An attention item carries no figure, so the strip borrows the row's.
 */
export function pointsFigure(balanceText: string | undefined): string | null {
  const m = balanceText?.match(/^(-?[\d,]+) pts$/);
  return m ? m[1] : null;
}

export interface PointsMetrics {
  portfolio_total: number;
  unvalued: number;
  account_count: number;
  stale_count: number;
  expiring_count: number;
  expiring_names: string;
}

export interface PointsDto {
  today: string;
  has_programs: boolean;
  programs: Record<string, number>;
  accounts: PointsAccount[];
  manage: ManageAccount[];
  account_choices: Record<string, number>;
  metrics: PointsMetrics;
  attention: AttentionItem[];
  kind_totals: KindTotal[];
  filters: string[];
  filter: string;
  /** (kind, text) when there is nothing to render. "info" for an empty
   *  portfolio, "success" for a filter that matched nothing -- opposite news. */
  empty: ["info" | "success", string] | null;
  review_queue: {
    total: number;
    truncated: boolean;
    caption: string | null;
    items: {
      // `program_name` and `account_label`, NOT a single `title`. The port
      // declared a `title` the server has never sent and the page did
      // `item.title.replace(...)` on it, so the review queue crashed the whole
      // Points page the moment it held anything. It went unnoticed because the
      // database local dev was pointed at had an empty queue -- the same shape
      // as every other defect found on 2026-09-09: a branch no data reached.
      // The live view renders `**{program_name}** · {account_label}`
      // (6_Points.py:86), which is what this now mirrors.
      program_name: string;
      account_label: string;
      source: string;
      points: string;
      kind: string;
      effective_date: string;
      excerpt: string | null;
      approve_key: string;
      reject_key: string;
    }[];
  };
  record_form_expanded: boolean;
  cipher: { available: boolean; error: string | null; caption: [Tone, string] | null };
}

export async function fetchPoints(show?: string): Promise<PointsDto> {
  // The "Show" filter. Filtering happens server-side, through the same
  // `visible_accounts` the API uses everywhere else, so there is one
  // definition of what "Expiring soon" means.
  const q = show ? `?show=${encodeURIComponent(show)}` : "";
  const res = await authedFetch(`/api/points${q}`);
  return (await orThrow(res, "Could not load points")) as PointsDto;
}

export async function reviewEvent(
  eventId: number,
  state: "approved" | "rejected",
): Promise<void> {
  const res = await authedFetch(`/api/points/events/${eventId}/review`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ state }),
  });
  await orThrow(res, "Could not update the event");
}

export async function recordBalance(input: {
  account_id: number;
  observed: number;
  as_of: string;
  note?: string;
}): Promise<string> {
  const res = await authedFetch("/api/points/balances", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = (await orThrow(res, "Could not record the balance")) as {
    message: string;
  };
  // The drift message. Surfaced verbatim: non-zero drift is the signal that
  // ingestion is missing deltas, and rewording it in the browser would put a
  // second interpretation between the number and the person reading it.
  return body.message;
}

export async function createAccount(input: {
  program_id: number;
  label: string;
  member_number?: string;
}): Promise<{ id: number; member_number_stored: boolean; message?: string }> {
  const res = await authedFetch("/api/points/accounts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return (await orThrow(res, "Could not add the account")) as {
    id: number;
    member_number_stored: boolean;
    message?: string;
  };
}

/**
 * Fetch one decrypted member number.
 *
 * A POST, matching the server: a GET would put an id that resolves to a secret
 * into browser history, proxy logs and prefetch. The value is returned to the
 * caller and never stored anywhere by this module.
 */
export async function revealMemberNumber(accountId: number): Promise<string> {
  const res = await authedFetch(`/api/points/accounts/${accountId}/reveal`, {
    method: "POST",
  });
  const body = (await orThrow(res, "Could not reveal the member number")) as {
    member_number: string;
  };
  return body.member_number;
}

/**
 * One credit's booking code, decrypted by the server for this call only.
 *
 * A POST for the same reason revealMemberNumber is one, and the value is
 * returned to the caller and stored nowhere by this module.
 */
export async function revealInstrumentCode(instrumentId: number): Promise<string> {
  const res = await authedFetch(`/api/points/instruments/${instrumentId}/reveal`, {
    method: "POST",
  });
  const body = (await orThrow(res, "Could not reveal the booking code")) as {
    code: string;
  };
  return body.code;
}

export async function deleteAccount(accountId: number): Promise<void> {
  const res = await authedFetch(`/api/points/accounts/${accountId}`, {
    method: "DELETE",
  });
  await orThrow(res, "Could not delete the account");
}

/**
 * Set an account's whole-balance expiry by hand, or clear it with null.
 *
 * A date latches: no capture or import overwrites it. Null clears the date and
 * the latch, so capture can write it again.
 */
export async function setAccountExpiry(
  accountId: number,
  expiresOn: string | null,
): Promise<void> {
  const res = await authedFetch(`/api/points/accounts/${accountId}/expiry`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ expires_on: expiresOn }),
  });
  await orThrow(res, "Could not set the expiry date");
}

export async function markInstrumentUsed(instrumentId: number): Promise<void> {
  const res = await authedFetch(
    `/api/points/instruments/${instrumentId}/used`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
  );
  await orThrow(res, "Could not mark it used");
}

/**
 * Undo the newest recorded change to a credit's amount. The server refuses
 * (409, with a reason) when an import changed the amount since.
 */
export async function revertInstrument(instrumentId: number): Promise<void> {
  const res = await authedFetch(
    `/api/points/instruments/${instrumentId}/revert`,
    { method: "POST" },
  );
  await orThrow(res, "Could not revert it");
}

const BASIS_LABEL: Record<string, { text: string; className: string }> = {
  authoritative: { text: "Authoritative", className: "text-green-400" },
  derived: { text: "Derived", className: "text-amber-400" },
  none: { text: "No data", className: "text-red-400" },
};

/**
 * The provenance badge.
 *
 * Every balance on this page renders next to its basis, because a points
 * balance with no provenance is indistinguishable from a guess. An unknown
 * basis renders as "No data" rather than as nothing, so a new value added
 * server-side degrades to the most cautious label rather than to a blank.
 */
export function basisBadge(basis: string): { text: string; className: string } {
  return BASIS_LABEL[basis] ?? BASIS_LABEL.none;
}

export interface AccountGroup extends KindTotal {
  accounts: PointsAccount[];
}

/**
 * The visible accounts under their kind, in the server's group order.
 *
 * The subtotal is the server's, over EVERY account, so a filter narrows the
 * rows without moving the figure, the same rule as the portfolio total. A kind
 * the filter emptied is left out rather than shown as a bare header.
 *
 * Empty accounts are left out too; `emptyAccounts` returns them for the folded
 * section below the groups. The subtotal still covers them.
 */
export function groupAccounts(
  accounts: PointsAccount[],
  totals: KindTotal[],
): AccountGroup[] {
  return totals
    .map((t) => ({
      ...t,
      accounts: accounts.filter((a) => !a.empty && a.row.kind === t.kind),
    }))
    .filter((g) => g.accounts.length > 0);
}

/** The accounts `groupAccounts` leaves out as empty, in arrival order. */
export function emptyAccounts(accounts: PointsAccount[]): PointsAccount[] {
  return accounts.filter((a) => a.empty);
}

/**
 * The credits listed inline under a row: live and worth something. A $0 or
 * spent credit stays in the expanded detail, where seeing "$0" is how you
 * know it was spent.
 */
export function inlineInstruments(items: InstrumentRow[]): InstrumentRow[] {
  return items.filter((i) => !i.spent && i.nonzero);
}

/**
 * The inline lines under a row: one total for each name the server totalled,
 * where its first credit stood, and every other credit on its own.
 */
export type InlineLine =
  | { kind: "credit"; credit: InstrumentRow }
  | { kind: "total"; total: InstrumentTotal };

export function inlineLines(items: InstrumentRow[], totals: InstrumentTotal[]): InlineLine[] {
  const byName = new Map(totals.map((t) => [t.name, t]));
  const shown = new Set<string>();
  return inlineInstruments(items).flatMap((credit): InlineLine[] => {
    const total = byName.get(credit.name);
    if (!total) return [{ kind: "credit", credit }];
    if (shown.has(total.name)) return [];
    shown.add(total.name);
    return [{ kind: "total", total }];
  });
}

/** "+2,400" or "−25,000"; null when nothing moved. */
export function changeText(change: number | null): string | null {
  if (!change) return null;
  return `${change > 0 ? "+" : "−"}${Math.abs(change).toLocaleString("en-US")}`;
}

/**
 * When a capture last wrote a balance, for the line that answers "did last night's
 * run land?".
 *
 * The NEWEST capture date across the portfolio, not the oldest: one program
 * retired or signed out should not make a good run look like it never happened.
 * Null when no balance rests on a capture, which is a portfolio nothing has read.
 */
export function lastCapture(dto: Pick<PointsDto, "accounts">): string | null {
  const days = dto.accounts
    .map((a) => a.capture?.as_of)
    .filter((d): d is string => typeof d === "string");
  return days.length === 0 ? null : days.reduce((a, b) => (a > b ? a : b));
}

/**
 * "today", "yesterday", "3 days ago", or null when nothing has been captured.
 *
 * `ago` owns the words and the day arithmetic. Null rather than its "never",
 * because the line is hidden entirely for a portfolio no capture has written.
 */
export function capturedLabel(day: string | null, today: string): string | null {
  return day === null ? null : ago(day, new Date(`${today}T12:00:00`));
}
